import { createRouter, createWebHashHistory, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

function isEmbeddedAppRuntime() {
  if (typeof window === 'undefined') {
    return false
  }

  return window.location.protocol === 'file:' || window.location.host === 'appassets.androidplatform.net'
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { guest: true },
  },
  {
    path: '/set-password',
    name: 'set-password',
    component: () => import('@/views/SetPasswordView.vue'),
    meta: { guest: true },
  },
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/dashboard/:groupId?',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { auth: true },
  },
  {
    path: '/widget/:widgetId',
    name: 'detail',
    component: () => import('@/views/DetailView.vue'),
    meta: { auth: true },
  },
  {
    path: '/server',
    redirect: '/server/overview',
  },
  {
    path: '/server/:section(overview|system|docker|backup)',
    name: 'server',
    component: () => import('@/views/ServerView.vue'),
    meta: { auth: true, admin: true },
  },
  {
    path: '/admin',
    component: () => import('@/views/AdminView.vue'),
    meta: { auth: true, admin: true },
    children: [
      {
        path: 'sensors/:id',
        name: 'admin-sensor-detail',
        component: () => import('@/views/admin/AdminSensorDetail.vue'),
      },
      {
        path: 'widgets/:id',
        name: 'admin-widget-detail',
        component: () => import('@/views/admin/AdminWidgetDetail.vue'),
      },
      {
        path: 'groups/:id',
        name: 'admin-group-detail',
        component: () => import('@/views/admin/AdminGroupDetail.vue'),
      },
      {
        path: 'locations/:id',
        name: 'admin-location-detail',
        component: () => import('@/views/admin/AdminLocationDetail.vue'),
      },
      {
        path: 'users/:id',
        name: 'admin-user-detail',
        component: () => import('@/views/admin/AdminUserDetail.vue'),
      },
      {
        path: 'devices/:id',
        name: 'admin-device-detail',
        component: () => import('@/views/admin/AdminDeviceDetail.vue'),
      },
      {
        path: 'cron/:id',
        name: 'admin-cron-detail',
        component: () => import('@/views/admin/AdminCronDetail.vue'),
      },
      {
        path: 'firmware/:id',
        name: 'admin-firmware-detail',
        component: () => import('@/views/admin/AdminFirmwareDetail.vue'),
      },
      {
        path: ':section?',
        name: 'admin',
        component: () => import('@/views/admin/AdminListView.vue'),
      },
    ],
  },
  {
    path: '/impress',
    name: 'impress',
    component: () => import('@/views/ImpressView.vue'),
  },
  {
    path: '/einstellungen',
    name: 'settings',
    component: () => import('@/views/UserSettingsView.vue'),
    meta: { auth: true },
  },
  {
    path: '/documents',
    name: 'documents',
    component: () => import('@/views/DocumentsView.vue'),
    meta: { auth: true, app: 'documents' },
  },
  {
    path: '/share/:token',
    name: 'public-document',
    component: () => import('@/views/PublicDocumentView.vue'),
    meta: { public: true },
  },
  {
    path: '/documents/:id',
    name: 'document-detail',
    component: () => import('@/views/DocumentDetailView.vue'),
    meta: { auth: true, app: 'documents' },
  },
  {
    path: '/inventory',
    name: 'inventory',
    component: () => import('@/views/InventoryView.vue'),
    meta: { auth: true, app: 'inventory' },
  },
  {
    path: '/inventory/locations',
    name: 'inventory-locations',
    component: () => import('@/views/InventoryLocationsView.vue'),
    meta: { auth: true, app: 'inventory' },
  },
  {
    path: '/inventory/categories',
    name: 'inventory-categories',
    component: () => import('@/views/InventoryCategoriesView.vue'),
    meta: { auth: true, app: 'inventory' },
  },
  {
    path: '/inventory/labels',
    name: 'inventory-labels',
    component: () => import('@/views/InventoryLabelsView.vue'),
    meta: { auth: true, app: 'inventory' },
  },
  {
    path: '/inventory/:id',
    name: 'inventory-detail',
    component: () => import('@/views/InventoryDetailView.vue'),
    meta: { auth: true, app: 'inventory' },
  },
  {
    path: '/hausverwaltung',
    name: 'hausverwaltung',
    component: () => import('@/views/billing/BillingOverview.vue'),
    meta: { auth: true, app: 'hausverwaltung' },
  },
  {
    // Ohne :kind startet die Seite mit Wasser — der bisherige Pfad bleibt gültig.
    path: '/hausverwaltung/zaehler-scan/:kind(water|gas|electricity)?',
    name: 'hausverwaltung-zaehler-scan',
    component: () => import('@/views/billing/MeterScanView.vue'),
    meta: { auth: true, app: 'hausverwaltung' },
  },
  {
    path: '/hausverwaltung/zaehler-pruefen',
    name: 'hausverwaltung-zaehler-pruefen',
    component: () => import('@/views/billing/MeterScanQueueView.vue'),
    meta: { auth: true, app: 'hausverwaltung' },
  },
  {
    path: '/hausverwaltung/properties/:id',
    redirect: (to) => `/hausverwaltung/properties/${to.params.id}/overview`,
  },
  {
    path: '/hausverwaltung/properties/:id/:tab(overview|units|sensors|tenancies|documents|expenses|income|profit|rooms|grundrisse)',
    name: 'hausverwaltung-property',
    component: () => import('@/views/billing/PropertyManageView.vue'),
    meta: { auth: true },
  },
  {
    path: '/hausverwaltung/properties/:id/invoices',
    redirect: (to) => `/hausverwaltung/properties/${to.params.id}/expenses`,
  },
  {
    path: '/hausverwaltung/units/:id',
    name: 'hausverwaltung-unit',
    component: () => import('@/views/billing/RentalUnitManageView.vue'),
    meta: { auth: true },
  },
  {
    path: '/hausverwaltung/units/:id/grundriss',
    name: 'hausverwaltung-unit-floorplan',
    component: () => import('@/views/billing/FloorPlanEditorView.vue'),
    meta: { auth: true },
  },
  {
    path: '/hausverwaltung/tenancies/:id',
    name: 'hausverwaltung-tenancy',
    component: () => import('@/views/billing/TenancyDetailView.vue'),
    meta: { auth: true },
  },
  {
    path: '/hausverwaltung/rooms/:id',
    name: 'hausverwaltung-room',
    component: () => import('@/views/billing/RoomDetailView.vue'),
    meta: { auth: true },
  },
  {
    path: '/hausverwaltung/units/:id/documents',
    name: 'hausverwaltung-unit-documents',
    component: () => import('@/views/hausverwaltung/UnitDocumentsView.vue'),
    meta: { auth: true },
  },
  {
    path: '/hausverwaltung/periods/:id',
    name: 'hausverwaltung-period',
    component: () => import('@/views/billing/BillingPeriodView.vue'),
    meta: { auth: true },
  },
  {
    path: '/hausverwaltung/cost-entries/:id',
    name: 'hausverwaltung-cost-entry',
    component: () => import('@/views/billing/CostEntryDetailView.vue'),
    meta: { auth: true },
  },
  {
    path: '/hausverwaltung/results/:id',
    name: 'hausverwaltung-result',
    component: () => import('@/views/billing/BillingResultView.vue'),
    meta: { auth: true },
  },
  {
    path: '/hausverwaltung/periods/:id/preview',
    name: 'hausverwaltung-period-preview',
    component: () => import('@/views/billing/BillingPreviewView.vue'),
    meta: { auth: true },
  },
  {
    path: '/todos',
    name: 'todos',
    component: () => import('@/views/TodosView.vue'),
    meta: { auth: true, app: 'todos' },
  },
  {
    path: '/flights',
    name: 'flights',
    component: () => import('@/views/FlightsView.vue'),
    meta: { auth: true, app: 'flights' },
  },
  // Legacy billing redirects
  { path: '/billing', redirect: '/hausverwaltung' },
  { path: '/billing/properties/:id', redirect: (to) => `/hausverwaltung/properties/${to.params.id}` },
  { path: '/billing/periods/:id', redirect: (to) => `/hausverwaltung/periods/${to.params.id}` },
  { path: '/billing/results/:id', redirect: (to) => `/hausverwaltung/results/${to.params.id}` },
]

const router = createRouter({
  history: isEmbeddedAppRuntime() ? createWebHashHistory() : createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // Try auto-login via refresh token cookie before checking auth
  if (to.meta.auth && !auth.isAuthenticated) {
    await auth.tryAutoLogin()
  }

  // Load public settings once after authentication
  if (auth.isAuthenticated) {
    const { useSettingsStore } = await import('@/stores/settings')
    const settings = useSettingsStore()
    if (!settings.loaded) {
      settings.loadPublicSettings()
    }
  }

  if (to.meta.auth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.admin && !auth.isAdmin) {
    return { name: 'dashboard' }
  }

  if (to.meta.app && !auth.canAccessApp(to.meta.app)) {
    return { name: 'dashboard' }
  }

  if (to.meta.guest && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }
})

export default router
